import React from 'react';
import styled from 'styled-components';
import ServicesSection from '../components/ServicesSection';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';

const IntroWrapper = styled.section`
  position: relative;
  padding: 130px 20px 110px;
  background: linear-gradient(135deg, rgba(247, 248, 252, 1) 0%, rgba(236, 240, 251, 1) 60%, rgba(229, 233, 249, 1) 100%);

  @media (max-width: 768px) {
    padding: 110px 18px 90px;
  }

  @media (max-width: 480px) {
    padding: 94px 16px 74px;
  }
`;

const PageContainer = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 26px;

  @media (max-width: 768px) {
    gap: 22px;
  }
`;

const PageTag = styled.span`
  width: fit-content;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(12, 77, 162, 0.15);
  color: var(--primary);
  letter-spacing: 0.3em;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  @media (max-width: 768px) {
    align-self: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-size: clamp(2.4rem, 4vw, 3.5rem);
  margin: 0;
  color: var(--secondary);
  line-height: 1.1;

  @media (max-width: 768px) {
    line-height: 1.2;
  }
`;

const IntroText = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 1.1rem;
  line-height: 1.8;
  max-width: 820px;

  @media (max-width: 768px) {
    font-size: 1.02rem;
  }
`;

const HighlightsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 22px;
  margin-top: 28px;

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const StatCard = styled.div`
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-hover);
  }

  @media (max-width: 600px) {
    padding: 24px;
  }
`;

const StatValue = styled.span`
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 700;
  color: var(--primary);
`;

const StatLabel = styled.span`
  color: var(--text-muted);
  font-size: 0.98rem;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const ServicesPage = () => {
  const { t } = useLanguage();
  const stats = t('pages.services.stats');

  return (
    <>
      <IntroWrapper>
        <PageContainer>
          <PageTag>{t('pages.services.tag')}</PageTag>
          <PageTitle>{t('pages.services.title')}</PageTitle>
          <IntroText>{t('pages.services.intro')}</IntroText>
          <HighlightsBar>
            {stats.map((stat) => (
              <StatCard key={stat.label}>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </HighlightsBar>
        </PageContainer>
      </IntroWrapper>
      <ServicesSection />
      <CTASection />
      <Footer />
    </>
  );
};

export default ServicesPage;
